<template>
  <AnalyticsCard
    title="Devices and browsers"
    :subtitle="activeTab === 'platform' ? 'Device type, and whether it converts' : 'Share of visitors'"
    :loading="loading"
    :empty="!loading && rows.length === 0"
    empty-title="No device data yet"
    skeleton="donut"
    :height="288"
  >
    <template #actions>
      <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        <button v-for="tab in TABS" :key="tab.key" type="button" @click="activeTab = tab.key"
          class="px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all"
          :class="activeTab === tab.key
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
          {{ tab.label }}
        </button>
      </div>
    </template>

    <div class="p-5 flex flex-col sm:flex-row items-center gap-6">
      <div ref="el" class="w-40 h-40 flex-shrink-0"></div>

      <div class="flex-1 min-w-0 w-full space-y-2.5">
        <div v-for="(row, idx) in rows" :key="row.name" class="flex items-center gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ backgroundColor: color(idx) }"></span>
          <span class="text-sm text-gray-700 dark:text-gray-200 truncate flex-1 min-w-0">{{ row.name }}</span>
          <!-- Conversion only exists per device type. Showing "converts at 4%"
               against a browser version would imply a comparison the data does
               not support. -->
          <span v-if="activeTab === 'platform' && row.conversions > 0"
            class="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums flex-shrink-0">
            {{ Math.round(row.conversion_rate * 100) }}% booked
          </span>
          <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0 w-10 text-right">
            {{ Math.round(row.share * 100) }}%
          </span>
        </div>
      </div>
    </div>
  </AnalyticsCard>
</template>

<script setup>
import { ref, computed } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { useAnalyticsChart, SERIES_COLORS } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  devices:    { type: Object, default: () => ({}) },
  conversion: { type: Array, default: () => [] },
  loading:    { type: Boolean, default: false },
})

const TABS = [
  { key: 'platform', label: 'Device' },
  { key: 'os',       label: 'OS' },
  { key: 'browser',  label: 'Browser' },
]

const activeTab = ref('platform')

const rows = computed(() => {
  const list = props.devices?.[activeTab.value] ?? []
  if (activeTab.value !== 'platform') return list
  const byName = new Map(props.conversion.map(c => [c.name, c]))
  return list.map(row => ({ ...row, ...(byName.get(row.name) || {}) }))
})

function color(idx) {
  return SERIES_COLORS[idx % SERIES_COLORS.length]
}

const { el } = useAnalyticsChart((_node, tokens) => {
  if (!rows.value.length) return null

  return {
    chart: { type: 'pie', backgroundColor: 'transparent', margin: [0, 0, 0, 0], spacing: [0, 0, 0, 0], height: 160 },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: '#111827',
      borderColor: 'transparent',
      borderRadius: 10,
      shadow: false,
      style: { color: '#f9fafb', fontSize: '12px' },
      pointFormat: '<b>{point.name}</b>: {point.percentage:.1f}%',
    },
    plotOptions: {
      pie: {
        innerSize: '64%',
        dataLabels: { enabled: false },
        borderWidth: 2,
        // Matches the card it sits on so the donut segments stay separated in
        // both themes rather than merging into one ring on dark.
        borderColor: tokens.card,
      },
    },
    series: [{
      colorByPoint: true,
      colors: SERIES_COLORS,
      data: rows.value.map(row => ({ name: row.name, y: row.visitors })),
    }],
  }
}, [rows])
</script>
